import MUIStack from "@mui/material/Stack";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from "@mui/material/TextField";
import Location from "../../Utility/Crud/Location";
import Region from "../../Utility/Crud/Region";
import InfoItem from "../InfoItem";
import ReportSetting from "../ReportSetting";
import MainContext from "../../Context/MainContext/MainContext";
import User from "../../Utility/Crud/User";

export default class ReportSettingRegionLocation extends ReportSetting {
  static contextType = MainContext; // For legacy class components

  /**
   * Renders location dropdown for selecting multiple locations.
   */
  renderLocationDropdown() {
    const handleLocationChange = (e, locations) => {
      this.handleSettingChange(
        "locationIds",
        locations.map((location) => location.id),
      );
    };
    const locations = this.getAccessibleLocations();

    return (
      <MUIAutocomplete
        options={locations}
        multiple
        value={Location.read(locations, [
          {
            key: "id",
            value: this.state.settings.locationIds || [],
          },
        ])}
        getOptionLabel={(option) => Location.getDisplayName(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={handleLocationChange}
        sx={{ width: "300px" }}
        renderInput={(params) => {
          return (
            <MUITextField
              {...params}
              size="small"
              label="Location"
              variant="standard"
              InputProps={{
                ...params.InputProps,
              }}
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
      />
    );
  }

  /**
   * Get filtered locations based on current user and any Sales links.
   * @returns {array} locations
   */
  getAccessibleLocations() {
    const currentUser = this.context.currentUser;
    const locationsForScope = new Set(
      User.getFilteredLocations(
        currentUser,
        this.context.userprofileLocationsIndexed,
        this.context.locationsIndexed,
      ),
    );
    const userIds = [];
    if (this.props.restrictToCurrentUserLocations) {
      Object.values(this.context.salesPcrLinksIndexed)
        .map((link) => {
          if (link.patientCareRepId === currentUser.id) {
            userIds.push(link.salesRepId);
          }
        })
        .filter((x) => x);

      userIds.forEach((userId) => {
        User.getFilteredLocations(
          this.context.usersIndexed[userId],
          this.context.userprofileLocationsIndexed,
          this.context.locationsIndexed,
        ).forEach((location) => locationsForScope.add(location));
      });
    } else {
      return Location.sort(Object.values(this.context.locationsIndexed));
    }
    return Array.from(locationsForScope);
  }

  /**
   * Returns the list of accessible regions for the given region type.
   * This encapsulates the logic for user-based filtering, sorting, and type filtering.
   */
  getAccessibleRegions() {
    const regionsForScope = this.props.restrictToCurrentUserLocations
      ? (() => {
          const userLocations = this.getAccessibleLocations();
          const currentUserRegions = new Set(
            userLocations
              .map((location) => this.context.regionsIndexed[location.regionId])
              .filter((region) => region),
          );
          return Array.from(currentUserRegions);
        })()
      : Object.values(this.context.regionsIndexed);

    return Region.sort(regionsForScope).filter(
      (region) => region.regionTypeTermId === this.props.regionId,
    );
  }

  /**
   * Renders region dropdown for selecting a region.
   */
  renderRegionDropdown() {
    const handleRegionChange = (e, regions) => {
      this.handleSettingChange(
        "regionIds",
        regions.map((region) => region.id),
      );
    };

    const regions = this.getAccessibleRegions();

    return (
      <MUIAutocomplete
        options={regions}
        multiple
        value={Region.read(regions, [
          {
            key: "id",
            value: this.state.settings.regionIds || [],
          },
        ])}
        getOptionLabel={(option) => Region.getDisplayName(option)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={handleRegionChange}
        sx={{ width: "300px" }}
        renderInput={(params) => {
          return (
            <MUITextField
              {...params}
              size="small"
              label="Region"
              variant="standard"
              InputProps={{
                ...params.InputProps,
              }}
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
      />
    );
  }

  renderContent() {
    return (
      <MUIStack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "center",
        }}
      >
        {this.renderRegionDropdown()}
        {this.renderLocationDropdown()}
      </MUIStack>
    );
  }

  renderReadOnly() {
    // Current user's accessible regions and locations if restrictToCurrentUserLocations is true, otherwise all regions and locations
    const regions = this.getAccessibleRegions();

    const locations = this.getAccessibleLocations();

    // Currently selected regions and locations based on settings
    const selectedRegions = Region.read(regions, [
      { key: "id", value: this.state.settings.regionIds },
    ]);

    const selectedLocations = Location.read(locations, [
      { key: "id", value: this.state.settings.locationIds },
    ]);

    // Determine display values for regions and locations
    const regionNames =
      this.state.settings.regionIds?.length > 0
        ? selectedRegions
            .map((region) => Region.getDisplayName(region))
            .join(", ")
        : "All Regions";
    const locationNames =
      this.state.settings.locationIds?.length > 0
        ? selectedLocations
            .map((location) => Location.getDisplayName(location))
            .join(", ")
        : "All Locations";

    // If restrictToCurrentUserLocations is true and neither are selected, show all locations and regions for the user
    if (
      this.props.restrictToCurrentUserLocations &&
      selectedRegions.length === 0 &&
      selectedLocations.length === 0
    ) {
      const regionNames = regions
        .map((region) => Region.getDisplayName(region))
        .join(", ");
      const locationNames = locations
        .map((location) => Location.getDisplayName(location))
        .join(", ");
      return (
        <>
          <InfoItem label="Region" value={regionNames} />
          <InfoItem label="Location" value={locationNames} />
        </>
      );
    }

    return (
      <>
        <InfoItem label="Region" value={regionNames} />
        <InfoItem label="Location" value={locationNames} />
      </>
    );
  }
}
